import {useCallback, useEffect, useState} from 'react';
import {
    Box,
    Paper,
    Stack,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Skeleton,
    Button,
    TablePagination,
    Switch,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    MenuItem,
    IconButton,
} from '@mui/material';
import {useTheme} from '@mui/material/styles';
import {Add, DeleteOutline, EditOutlined} from '@mui/icons-material';
import {
    OperationLogRuleService,
    OperationLogRuleVO,
    OperationLogRulePage,
    OperationLogRuleSaveBO,
} from '@/services/manageService/operationLogRuleService';
import {SnackbarField, InputField} from '@/components/common';
import {toolbarFilterFieldSx, toolbarFilterLabelProps} from '@/pages/components/manage/sys/operation-log/toolbarStyles';

const emptyForm: OperationLogRuleSaveBO = {
    pageName: '',
    featureName: '',
    urlPattern: '',
    httpMethod: 'WRITE',
    serviceName: 'audit',
    enabled: 1,
    sortOrder: 100,
    remark: '',
};

export interface OperationLogRuleListProps {
    /** When set, show Back and fill parent Slide panel (embedded from Operation Log). */
    onBack?: () => void;
}

export default function OperationLogRuleList({onBack}: OperationLogRuleListProps) {
    const theme = useTheme();
    const embedded = Boolean(onBack);
    const [data, setData] = useState<OperationLogRulePage>({
        records: [], total: 0, size: 25, current: 1, pages: 0,
    });
    const [loading, setLoading] = useState(true);
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
        open: false, message: '', severity: 'success',
    });
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);

    const [pageName, setPageName] = useState('');
    const [featureName, setFeatureName] = useState('');
    const [urlPattern, setUrlPattern] = useState('');
    const [enabled, setEnabled] = useState('');

    const [dialogOpen, setDialogOpen] = useState(false);
    const [form, setForm] = useState<OperationLogRuleSaveBO>(emptyForm);
    const [saving, setSaving] = useState(false);

    const fetchData = useCallback(async (pageNum = 1, pageSize = 25) => {
        setLoading(true);
        try {
            const res = await OperationLogRuleService.queryPage({
                pageNum,
                pageSize,
                pageName: pageName.trim() || undefined,
                featureName: featureName.trim() || undefined,
                urlPattern: urlPattern.trim() || undefined,
                enabled: enabled === '' ? undefined : Number(enabled),
            });
            setData(res);
        } catch (e: any) {
            setSnackbar({open: true, message: e?.message || 'Failed to load data', severity: 'error'});
        } finally {
            setLoading(false);
        }
    }, [pageName, featureName, urlPattern, enabled]);

    useEffect(() => {
        fetchData(page + 1, rowsPerPage);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, rowsPerPage]);

    const handleSearch = () => {
        setPage(0);
        fetchData(1, rowsPerPage);
    };

    const openCreate = () => {
        setForm({...emptyForm});
        setDialogOpen(true);
    };

    const openEdit = (row: OperationLogRuleVO) => {
        setForm({
            id: row.id,
            pageName: row.pageName || '',
            featureName: row.featureName || '',
            urlPattern: row.urlPattern || '',
            httpMethod: row.httpMethod || '*',
            serviceName: row.serviceName || '*',
            enabled: row.enabled ?? 1,
            sortOrder: row.sortOrder ?? 100,
            remark: row.remark || '',
        });
        setDialogOpen(true);
    };

    const handleSave = async () => {
        if (!form.pageName.trim() || !form.featureName.trim() || !form.urlPattern.trim()) {
            setSnackbar({open: true, message: 'Page / Feature / URL are required', severity: 'error'});
            return;
        }
        setSaving(true);
        try {
            if (form.id) {
                await OperationLogRuleService.update(form);
            } else {
                await OperationLogRuleService.save(form);
            }
            setDialogOpen(false);
            setSnackbar({open: true, message: 'Saved', severity: 'success'});
            fetchData(page + 1, rowsPerPage);
        } catch (e: any) {
            setSnackbar({open: true, message: e?.message || 'Save failed', severity: 'error'});
        } finally {
            setSaving(false);
        }
    };

    const toggleEnabled = async (row: OperationLogRuleVO) => {
        if (row.id == null) return;
        const next = row.enabled === 1 ? 0 : 1;
        try {
            await OperationLogRuleService.updateEnabled(row.id, next);
            fetchData(page + 1, rowsPerPage);
        } catch (e: any) {
            setSnackbar({open: true, message: e?.message || 'Update failed', severity: 'error'});
        }
    };

    const handleDelete = async (row: OperationLogRuleVO) => {
        if (row.id == null) return;
        if (!window.confirm(`Delete rule "${row.pageName} / ${row.featureName}"?`)) return;
        try {
            await OperationLogRuleService.remove(row.id);
            setSnackbar({open: true, message: 'Deleted', severity: 'success'});
            fetchData(page + 1, rowsPerPage);
        } catch (e: any) {
            setSnackbar({open: true, message: e?.message || 'Delete failed', severity: 'error'});
        }
    };

    return (
        <Box sx={{
            p: embedded ? 0 : 2,
            bgcolor: 'transparent',
            height: embedded ? '100%' : 'calc(100vh - 120px)',
            minHeight: embedded ? 0 : '70vh',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
        }}>
                <Stack direction={{xs: 'column', sm: 'row'}} justifyContent="space-between"
                       alignItems={{xs: 'stretch', sm: 'center'}} gap={1} mb={2} flexShrink={0}>
                    <Typography variant="h6" fontWeight={700} color="text.primary" sx={{flexShrink: 0}}>
                        Operation Log Rules
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="flex-end" flexWrap="nowrap"
                           sx={{minWidth: 0, overflowX: 'auto', overflowY: 'visible'}}>
                        <InputField size="small" label="Page" value={pageName}
                                    onChange={e => setPageName(e.target.value)}
                                    InputLabelProps={toolbarFilterLabelProps}
                                    sx={{width: 150, ...toolbarFilterFieldSx}}/>
                        <InputField size="small" label="Feature" value={featureName}
                                    onChange={e => setFeatureName(e.target.value)}
                                    InputLabelProps={toolbarFilterLabelProps}
                                    sx={{width: 150, ...toolbarFilterFieldSx}}/>
                        <InputField size="small" label="URL Pattern" value={urlPattern}
                                    onChange={e => setUrlPattern(e.target.value)}
                                    InputLabelProps={toolbarFilterLabelProps}
                                    sx={{width: 200, ...toolbarFilterFieldSx}}/>
                        <InputField size="small" label="Enabled" value={enabled}
                                    onChange={e => setEnabled(e.target.value)} select
                                    InputLabelProps={toolbarFilterLabelProps}
                                    sx={{width: 120, ...toolbarFilterFieldSx}}>
                            <MenuItem value="">All</MenuItem>
                            <MenuItem value="1">On</MenuItem>
                            <MenuItem value="0">Off</MenuItem>
                        </InputField>
                        <Button size="small" variant="contained" color="secondary"
                                onClick={handleSearch}>
                            Query
                        </Button>
                        <Button size="small" variant="contained" color="secondary" startIcon={<Add/>}
                                onClick={openCreate}>
                            Add Rule
                        </Button>
                        {onBack && (
                            <Button size="small" color="secondary" onClick={onBack}>
                                Back
                            </Button>
                        )}
                    </Stack>
                </Stack>

                <TableContainer
                    component={Paper}
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        overflow: 'auto',
                        boxShadow: theme.shadows[2],
                        bgcolor: 'background.paper',
                    }}
                >
                    <Table
                        size="small"
                        stickyHeader
                        sx={{
                            minWidth: 980,
                            '& .MuiTableCell-stickyHeader': {
                                bgcolor: 'background.paper',
                                zIndex: 3,
                                borderBottom: `1px solid ${theme.palette.divider}`,
                            },
                        }}
                    >
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{fontWeight: 700}}>Page</TableCell>
                                <TableCell sx={{fontWeight: 700}}>Feature</TableCell>
                                <TableCell sx={{fontWeight: 700}}>URL Pattern</TableCell>
                                <TableCell sx={{fontWeight: 700}}>Method</TableCell>
                                <TableCell sx={{fontWeight: 700}}>Service</TableCell>
                                <TableCell sx={{fontWeight: 700}}>Sort</TableCell>
                                <TableCell sx={{fontWeight: 700}}>Enabled</TableCell>
                                <TableCell sx={{fontWeight: 700}} align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                Array.from({length: 6}).map((_, i) => (
                                    <TableRow key={i}>
                                        {Array.from({length: 8}).map((__, j) => (
                                            <TableCell key={j}><Skeleton height={24}/></TableCell>
                                        ))}
                                    </TableRow>
                                ))
                            ) : data.records.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" sx={{py: 4, color: 'text.secondary'}}>
                                        No rules
                                    </TableCell>
                                </TableRow>
                            ) : (
                                data.records.map(row => (
                                    <TableRow key={row.id} hover>
                                        <TableCell>{row.pageName}</TableCell>
                                        <TableCell>{row.featureName}</TableCell>
                                        <TableCell sx={{fontFamily: 'monospace', fontSize: 13}}>
                                            {row.urlPattern}
                                        </TableCell>
                                        <TableCell>{row.httpMethod}</TableCell>
                                        <TableCell>{row.serviceName}</TableCell>
                                        <TableCell>{row.sortOrder}</TableCell>
                                        <TableCell>
                                            <Switch size="small" checked={row.enabled === 1}
                                                    onChange={() => toggleEnabled(row)}/>
                                        </TableCell>
                                        <TableCell align="right">
                                            <IconButton size="small" onClick={() => openEdit(row)}>
                                                <EditOutlined fontSize="small"/>
                                            </IconButton>
                                            <IconButton size="small" color="error" onClick={() => handleDelete(row)}>
                                                <DeleteOutline fontSize="small"/>
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                    <TablePagination
                        component="div"
                        count={data.total || 0}
                        page={page}
                        onPageChange={(_, p) => setPage(p)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={(e) => {
                            setRowsPerPage(parseInt(e.target.value, 10));
                            setPage(0);
                        }}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                        sx={{flexShrink: 0}}
                    />

                <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
                    <DialogTitle>{form.id ? 'Edit Rule' : 'Add Rule'}</DialogTitle>
                    <DialogContent>
                        <Stack spacing={1.5} mt={1}>
                            <InputField label="Page" value={form.pageName}
                                        onChange={e => setForm({...form, pageName: e.target.value})}/>
                            <InputField label="Feature" value={form.featureName}
                                        onChange={e => setForm({...form, featureName: e.target.value})}/>
                            <InputField label="URL Pattern" value={form.urlPattern}
                                        onChange={e => setForm({...form, urlPattern: e.target.value})}
                                        helperText="Ant path, e.g. /working-paper/**"/>
                            <InputField label="HTTP Method" value={form.httpMethod || '*'} select
                                        onChange={e => setForm({...form, httpMethod: e.target.value})}>
                                <MenuItem value="*">*</MenuItem>
                                <MenuItem value="WRITE">WRITE</MenuItem>
                                <MenuItem value="GET">GET</MenuItem>
                                <MenuItem value="POST">POST</MenuItem>
                                <MenuItem value="PUT">PUT</MenuItem>
                                <MenuItem value="PATCH">PATCH</MenuItem>
                                <MenuItem value="DELETE">DELETE</MenuItem>
                            </InputField>
                            <InputField label="Service" value={form.serviceName || '*'} select
                                        onChange={e => setForm({...form, serviceName: e.target.value})}>
                                <MenuItem value="*">*</MenuItem>
                                <MenuItem value="audit">audit</MenuItem>
                                <MenuItem value="account">account</MenuItem>
                                <MenuItem value="admin">admin</MenuItem>
                                <MenuItem value="auth">auth</MenuItem>
                            </InputField>
                            <InputField label="Sort Order" type="number" value={String(form.sortOrder ?? 100)}
                                        onChange={e => setForm({...form, sortOrder: Number(e.target.value) || 100})}/>
                            <InputField label="Remark" value={form.remark || ''}
                                        onChange={e => setForm({...form, remark: e.target.value})}/>
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
                        <Button variant="contained" disabled={saving} onClick={handleSave}>Save</Button>
                    </DialogActions>
                </Dialog>

                <SnackbarField
                    open={snackbar.open}
                    message={snackbar.message}
                    severity={snackbar.severity}
                    onClose={() => setSnackbar(s => ({...s, open: false}))}
                />
        </Box>
    );
}
